import { Dialog, Transition } from "@headlessui/react";
import React, { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonSecondary from "shared/Button/ButtonSecondary";

type CoordinateDecisionResolver = (keepCoordinates: boolean) => void;

/**
 * Confirmation non native afin que la décision soit également proposée sur
 * les navigateurs mobiles, qui peuvent ignorer un window.confirm différé.
 */
const useCoordinateRetentionConfirm = () => {
  const { t } = useTranslation();
  const [fieldLabel, setFieldLabel] = useState<string | null>(null);
  const resolverRef = useRef<CoordinateDecisionResolver | null>(null);

  const answer = useCallback((keepCoordinates: boolean) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setFieldLabel(null);
    resolve?.(keepCoordinates);
  }, []);

  const requestCoordinateRetention = useCallback((label: string) => {
    return new Promise<boolean>((resolve) => {
      // Si une demande précédente était encore ouverte, conserver ses
      // coordonnées est le choix le moins destructif.
      resolverRef.current?.(true);
      resolverRef.current = resolve;
      setFieldLabel(label);
    });
  }, []);

  useEffect(() => {
    return () => {
      resolverRef.current?.(true);
      resolverRef.current = null;
    };
  }, []);

  const coordinateRetentionDialog = (
    <Transition appear show={fieldLabel !== null} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[10000]"
        onClose={() => answer(true)}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-3xl bg-white p-6 text-left align-middle shadow-xl transition-all dark:bg-neutral-900">
                <Dialog.Title className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                  {t("coordinateRetentionTitle")}
                </Dialog.Title>

                <p className="mt-3 whitespace-pre-line text-sm text-neutral-600 dark:text-neutral-300">
                  {t("coordinateRetentionMessage", { field: fieldLabel || "" })}
                </p>

                <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                  <ButtonSecondary onClick={() => answer(false)}>
                    {t("coordinateRetentionClear")}
                  </ButtonSecondary>
                  <ButtonPrimary onClick={() => answer(true)}>
                    {t("coordinateRetentionKeep")}
                  </ButtonPrimary>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );

  return { requestCoordinateRetention, coordinateRetentionDialog };
};

export default useCoordinateRetentionConfirm;
